import {Fade, Popper, type PopperProps, type SxProps} from "@mui/material";
import {CustomPaper} from "../customPaper/CustomPaper";
import {useEffect, useRef} from "react";

export type CustomPopperProps = PopperProps & {
    children: React.ReactNode;
    paperSx?: SxProps;
    onClose?: () => void;
    /**
     * When true, skip the Fade wrapper and `transition` pipeline so the menu mounts/shows immediately.
     * Use for dense menus (e.g. agent picker) where the default Fade + unmount-on-exit causes a slow first open.
     */
    disableTransition?: boolean;
};

export const CustomPopper = function CustomPopper({
                                                      children,
                                                      sx,
                                                      paperSx,
                                                      onClose,
                                                      disableTransition = false,
                                                      ...props
                                                  }: CustomPopperProps) {
    const paperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!props.open) {
            return undefined;
        }
        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (paperRef.current?.contains(target)) {
                return;
            }
            onClose?.();
        };
        document.addEventListener("mousedown", handleMouseDown, true);
        return () =>
            document.removeEventListener("mousedown", handleMouseDown, true);
    }, [props.open]);

    useEffect(() => {
        if (!props.open) {
            return undefined;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose?.();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [props.open]);

    if (disableTransition) {
        return (
            <Popper
                modifiers={[
                    {
                        name: "offset",
                        options: {
                            offset: [0, 6],
                        },
                    },
                    {name: "preventOverflow", options: {padding: 8}},
                ]}
                transition={false}
                disablePortal
                sx={{zIndex: (theme) => theme.zIndex.modal, ...sx}}
                {...props}
            >
                <CustomPaper sx={paperSx} ref={paperRef}>
                    {children}
                </CustomPaper>
            </Popper>
        );
    }

    return (
        <Popper
            placement="top-start"
            modifiers={[
                {
                    name: "offset",
                    options: {
                        offset: [0, 6],
                    },
                },
                {name: "preventOverflow", options: {padding: 8}},
            ]}
            transition
            disablePortal
            sx={{zIndex: (theme) => theme.zIndex.modal, ...sx}}
            {...props}
        >
            {({TransitionProps}) => (
                <Fade {...TransitionProps} timeout={150}>
                    <CustomPaper sx={paperSx} ref={paperRef}>
                        {children}
                    </CustomPaper>
                </Fade>
            )}
        </Popper>
    );
}

